import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { fetchCommitteeById } from '../committeesApi';
import { committeeMemberRoleLabel, committeeTypeLabel } from '../committeeFormUtils';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './BoardPage.css';
import './DecisionFormPage.css';

export default function CommitteeDetailPage() {
  const { language, direction } = useLanguage();
  const t = useT();

  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';
  const { id: idParam } = useParams();
  const committeeId = Number(idParam);
  const invalidId = !Number.isFinite(committeeId) || committeeId < 1;
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(!invalidId);
  const [loadError, setLoadError] = useState('');
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setLoadError(t('common.noPerm')); setLoading(false); return; }
    if (invalidId) { setLoadError(t('committees.invalidId')); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setLoadError('');
      try {
        const row = await fetchCommitteeById(committeeId);
        if (!cancelled) setItem(row);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('committees.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, committeeId, invalidId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="decision-v2" dir={direction}>
      <div className="page-wrapper">
        {loadError && <div className="v2-error">{loadError}</div>}
        {loading && <div className="v2-loading">{t('committees.loading')}</div>}
        {!loading && item && (
          <div className="card">
            <div className="card-top">
              <div className="top-bar top-bar--single-line">
                <div className="page-title-row">
                  <h1 className="page-title">{t('committees.pageTitle')}<span className="page-title-icon"><Icon name="board" size={16} color="#0EA5A0" /></span></h1>
                </div>
                <div className="action-btns">
                  <button type="button" className="btn btn-ghost" onClick={() => navigate(`${basePath}/committees`)}>{t('common.back')}</button>
                  <button type="button" className="btn btn-primary" onClick={() => navigate(`${basePath}/committees/${item.id}/edit`)}>{t('committees.edit')}</button>
                </div>
              </div>
            </div>
            <div className="card-inner">
              <div style={{ display: 'flex', flexDirection: 'row', gap: 10, marginBottom: 18, alignItems: 'stretch' }}>
                {[t('committees.stepInfo'), t('committees.stepMembers'), t('committees.stepReview')].map((label, i) => {
                  const done = i < step;
                  const active = i === step;
                  return (
                    <div key={label} style={{ textAlign: 'center', flex: 1, color: active || done ? '#0EA5A0' : '#8AACAC', fontSize: '0.82rem' }}>
                      <button type="button" onClick={() => setStep(i)} style={{ width: '100%', border: `1.5px solid ${active || done ? '#0EA5A0' : '#DDE6E6'}`, background: active ? '#E0F7F6' : '#fff', padding: '10px 8px', fontWeight: 800, position: 'relative', cursor: 'pointer', color: 'inherit', fontFamily: 'Cairo, sans-serif' }}>
                        <span style={{ display: 'inline-block', minWidth: 20, marginInlineEnd: 6 }}>{done ? '✓' : String(i + 1)}</span>{label}
                        <span style={{ position: 'absolute', right: 0, left: 0, bottom: -1, height: 3, background: active ? '#0EA5A0' : 'transparent' }} />
                      </button>
                    </div>
                  );
                })}
              </div>

              {step === 0 && (
                <div className="mform">
                  <div className="mform__row mform__row--4">
                    <div className="mform__field"><label className="mform__label">{t('committees.lblName')}</label><div className="mform__input-wrap"><input className="mform__input" value={item.name || ''} disabled /></div></div>
                    <div className="mform__field"><label className="mform__label">{t('committees.lblType')}</label><div className="mform__input-wrap"><input className="mform__input" value={committeeTypeLabel(item.type, language)} disabled /></div></div>
                    <div className="mform__field"><label className="mform__label">{t('committees.lblCreatedAt')}</label><div className="mform__input-wrap"><input className="mform__input" value={item.createdAt || ''} dir="ltr" disabled /></div></div>
                    <div className="mform__field"><label className="mform__label">{t('committees.lblStatus')}</label><div className="mform__input-wrap"><input className="mform__input" value={item.isActive ? t('committees.active') : t('committees.inactive')} disabled /></div></div>
                  </div>
                  <div className="mform__row mform__row--4">
                    <div className="mform__field"><label className="mform__label">{t('committees.lblBranch')}</label><div className="mform__input-wrap"><input className="mform__input" value={item.branch || ''} disabled /></div></div>
                    <div className="mform__field mform__field--span3"><label className="mform__label">{t('committees.lblDescription')}</label><div className="mform__input-wrap"><input className="mform__input" value={item.description || ''} disabled /></div></div>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div>
                  <div className="mform__section-title"><Icon name="user" size={13} color="#02c3a6" /> {t('committees.lblMembers')} <span className="mform__section-badge">{(item.members || []).length}</span></div>
                  {(item.members || []).length > 0 ? (
                    <div className="mform__att-list">
                      {item.members.map((m) => (
                        <div key={m.id || `${m.boardMemberId}-${m.role}`} className="mform__att-item">
                          <span className="mform__att-type">{committeeMemberRoleLabel(m.role, language)}</span>
                          <span className="mform__att-name">{m.boardMemberName}</span>
                        </div>
                      ))}
                    </div>
                  ) : <div className="users-empty">{t('committees.noMembersInCommittee')}</div>}
                </div>
              )}

              {step === 2 && (
                <div style={{ border: '1px solid #e4ecf7', padding: 14 }}>
                  <div style={{ fontWeight: 800, color: '#00334a', marginBottom: 8 }}>{item.name || '—'}</div>
                  <div style={{ color: '#607d8b', marginBottom: 4 }}>{t('committees.lblType')}: {committeeTypeLabel(item.type, language)}</div>
                  <div style={{ color: '#607d8b', marginBottom: 4 }}>{t('committees.lblBranch')}: {item.branch || '—'}</div>
                  <div style={{ color: '#607d8b', marginBottom: 4 }}>{t('committees.lblMembers')}: {(item.members || []).length}</div>
                  <div style={{ color: '#607d8b' }}>{t('committees.lblDescription')}: {item.description || '—'}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
